/**
 * 開封・クリック計測の表示ロジック（純粋関数）.
 *
 * 設計書: `docs/design-docs/deliveries/open-click-analytics.md` 3.1 / 3.4 / 9.1、
 * `open-click-analytics.html` 図1〜図7。
 */
import {
  type ActionCategory,
  type DotReaction,
  knownBounceMessage,
  type ReactionStep,
  SAFETY_FILTER_ERROR,
  SEND_FAILURE_MESSAGES,
} from "constants/delivery-analytics";

/** 反応バー・行ティントの判定に使う宛先の反応 */
export type RecipientReaction = {
  openCount: number;
  clickCount: number;
  replied: boolean;
};

/** 不達チップを組み立てるための内訳 */
export type UndeliveredCounts = {
  total: number;
  bounced: number;
  rejected: number;
  failed: number;
  actionRequired: number;
};

/**
 * 宛先の反応を 6 段のどの段に置くかを決める（上から順に評価する）。
 * @param {RecipientReaction} reaction 宛先の反応
 * @return {ReactionStep} 反応の段
 */
export function reactionStep(reaction: RecipientReaction): ReactionStep {
  if (reaction.replied) return 6;
  if (reaction.clickCount >= 2) return 5;
  if (reaction.clickCount === 1) return 4;
  if (reaction.openCount >= 2) return 3;
  if (reaction.openCount === 1) return 2;
  return 1;
}

/**
 * 反応バーに添えるラベル文字（色に頼らず強さを二重に伝える）。
 * @param {RecipientReaction} reaction 宛先の反応
 * @return {string} ラベル（`返信` / `クリック 3` / `開封 1` / `反応なし`）
 */
export function reactionLabel(reaction: RecipientReaction): string {
  if (reaction.replied) return "返信";
  if (reaction.clickCount > 0) return `クリック ${reaction.clickCount}`;
  if (reaction.openCount > 0) return `開封 ${reaction.openCount}`;
  return "反応なし";
}

/**
 * 会社の行ティントに使う段（直近 N 日で最も強かった反応）。
 *
 * A4 は反応の回数を返さず 8 区間のドット列だけを返すため、
 * **「その反応が現れた区間数」を回数の下限として段を決める**
 * （1 区間に複数回あっても 1 と数えるので、実際より濃くは出ない）。
 * 正確な回数で塗り分けるには A4 に回数を足す必要がある（設計書 15 章 U14）。
 *
 * @param {ReadonlyArray<DotReaction>} dots 推移ドット列の反応
 * @return {ReactionStep} 反応の段（実際の回数に対する下限）
 */
export function companyReactionStep(
  dots: ReadonlyArray<DotReaction>,
): ReactionStep {
  const clicks = dots.filter(d => d === "click").length;
  const opens = dots.filter(d => d === "open").length;
  return reactionStep({
    openCount: opens,
    clickCount: clicks,
    replied: dots.includes("reply"),
  });
}

/**
 * 届かなかった宛先が「要対応」か。
 *
 * バックエンドの `is_action_required`（=「対応不要」以外）と同じ規則。
 * 分類が付いていない宛先（届いている宛先）は要対応ではない。
 * @param {ActionCategory | null | undefined} category 届かなかった宛先の分類
 * @return {boolean} 要対応なら true
 */
export function isActionRequired(
  category: ActionCategory | null | undefined,
): boolean {
  return (
    category !== null && category !== undefined && category !== "no_action"
  );
}

/**
 * 率を百分率の文字列にする。計測対象外・分母 0 は `—`（0% とは区別する）。
 * @param {number | null | undefined} rate 0..1 の比率
 * @param {number} [digits] 小数点以下の桁数（既定 1）
 * @return {string} 表示文字列
 */
export function formatRate(
  rate: number | null | undefined,
  digits = 1,
): string {
  if (rate === null || rate === undefined) return "—";
  return `${(rate * 100).toFixed(digits)}%`;
}

/**
 * y 軸の目盛ラベル（罫線が指す値をそのまま書く）。
 *
 * 罫線は `axisMax` の 1/4・1/2・3/4 にあり、`axisMax` は 0.1 刻みなので
 * 目盛は必ず 2.5% の倍数になる（`axisMax = 0.1` なら 2.5 / 5 / 7.5 / 10）。
 * 整数に丸めると 2.5 → 3、7.5 → 8 となって等間隔の罫線に対して刻みが
 * 不均等に見え、最大 0.5 ポイント実際と違う値を出してしまうため、
 * **整数にならないときだけ小数を出す**（`5%` はそのまま、`7.5%` は小数 1 桁）。
 *
 * 丸めを 2 桁にしているのは浮動小数の誤差（`0.3 * 25 = 7.499999…`）を
 * 落とすためで、上記の刻みなら結果は必ず 1 桁以内に収まる。
 * @param {number} rate 0..1 の比率
 * @return {string} 目盛の数値（`%` は含まない）
 */
export function axisTickLabel(rate: number): string {
  return String(Math.round(rate * 100 * 100) / 100);
}

/**
 * 3 桁区切りの数値表記。
 * @param {number | null | undefined} value 数値
 * @return {string} 表示文字列
 */
export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return value.toLocaleString("ja-JP");
}

/**
 * 不達チップの文言（0 件の項目は出さない可変表記）。
 * @param {UndeliveredCounts} counts 不達の内訳
 * @return {string | null} チップの文言。届かなかった宛先が無ければ null
 */
export function undeliveredChipText(counts: UndeliveredCounts): string | null {
  if (counts.total <= 0) return null;
  const parts: string[] = [];
  if (counts.bounced > 0) parts.push(`届かず戻った ${counts.bounced}`);
  if (counts.rejected > 0) parts.push(`送信取消 ${counts.rejected}`);
  if (counts.failed > 0) parts.push(`送信失敗 ${counts.failed}`);
  const breakdown = parts.length > 0 ? `（${parts.join(" / ")}）` : "";
  const actionRequired =
    counts.actionRequired > 0 ? `· うち要対応 ${counts.actionRequired} 件` : "";
  return `届かなかった ${counts.total} 件${breakdown}${actionRequired}`;
}

/** 届かなかった宛先に添える一文を組み立てるための入力 */
export type UndeliveredReason = {
  deliveryState: string;
  bounceType: string | null;
  detailType: string | null;
  errorMessage: string | null;
};

/**
 * 届かなかった宛先に添える一文（分類名は出さず、結果を日本語で書く）。
 * @param {UndeliveredReason} reason 宛先の不達情報
 * @return {string | null} 一文。届いている宛先は null
 */
export function undeliveredMessage(reason: UndeliveredReason): string | null {
  if (reason.deliveryState === "failed") {
    if (reason.errorMessage === SAFETY_FILTER_ERROR) {
      return SEND_FAILURE_MESSAGES.safetyFilter;
    }
    return reason.errorMessage
      ? SEND_FAILURE_MESSAGES.withError
      : SEND_FAILURE_MESSAGES.aborted;
  }
  if (reason.deliveryState !== "undelivered") return null;

  const known = knownBounceMessage(reason.bounceType, reason.detailType);
  if (known) return known;

  const detail = reason.detailType || "不明";
  if (reason.bounceType === "Permanent") {
    return `このアドレスには届きませんでした（理由コード: Permanent/${detail}）。連絡先の確認をおすすめします`;
  }
  if (reason.bounceType === "Transient") {
    return `一時的に届きませんでした（理由コード: Transient/${detail}）。次回は届く可能性があります`;
  }
  if (reason.bounceType) {
    return `届きませんでした。原因は特定できていません（理由コード: ${reason.bounceType}/${detail}）`;
  }
  // bounceType を持たない宛先（reject / 計測開始前の配信で status='bounced'
  // しか根拠が無い宛先）。API 側でも両者を区別できないため、理由コードを
  // 出さない未確定の文言に倒す。
  return "届きませんでした。原因は特定できていません";
}

/**
 * 内訳の横帯の幅（%）を最大剰余法で丸め、合計を 100% に合わせる。
 * @param {ReadonlyArray<number>} values 各区分の実数
 * @return {number[]} 各区分の幅（%）
 */
export function largestRemainderPercents(
  values: ReadonlyArray<number>,
): number[] {
  const total = values.reduce((sum, v) => sum + v, 0);
  if (total <= 0) return values.map(() => 0);

  const exact = values.map(v => (v * 100) / total);
  const floors = exact.map(v => Math.floor(v));
  let remaining = 100 - floors.reduce((sum, v) => sum + v, 0);

  const order = exact
    .map((v, index) => ({ index, fraction: v - Math.floor(v) }))
    .sort((a, b) => b.fraction - a.fraction);

  const result = [...floors];
  for (const entry of order) {
    if (remaining <= 0) break;
    result[entry.index] += 1;
    remaining -= 1;
  }
  return result;
}
